import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Empty, Flex, Input, Pagination, Segmented, Select, Spin, Tag, Tooltip, Typography } from "antd";
import { ArrowLeft, ChevronLeft, ChevronRight, Maximize, Minimize, Users, Volume2, VolumeX } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetProctoringSession } from "@hooks";
import { getKeycloakRealm } from "@/components/helpers";
import { CandidateTile, candidateName, examStateTag } from "@/components/proctoring/CandidateTile";
import { CandidateAudio, CandidateVideo } from "@/components/proctoring/CandidateVideo";
import { ProctorAssignmentsDrawer } from "@/components/proctoring/ProctorAssignmentsDrawer";
import { useProctoringRoom } from "@/components/proctoring/useProctoringRoom";

type Filter = "all" | "connected" | "disconnected";
const PAGE_SIZES = [4, 9, 16, 25, 36, 49, 100];

export const ProctoringWallPage = () => {
  const { t } = useTranslation();
  const { termId } = useParams();
  const navigate = useNavigate();
  const wallRef = useRef<HTMLDivElement>(null);

  const { data: session, isLoading, isError } = useGetProctoringSession(termId);
  const { media, status, error } = useProctoringRoom(session?.serverUrl, session?.token);

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [audioOn, setAudioOn] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const candidates = session?.candidates ?? [];
  const connectedCount = candidates.filter((c) => media[c.identity]?.connected).length;
  const receivedVideos = Object.values(media).filter((m) => m.video).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((c) => {
      const connected = !!media[c.identity]?.connected;
      if (filter === "connected" && !connected) return false;
      if (filter === "disconnected" && connected) return false;
      return !q || `${candidateName(c)} ${c.email ?? ""}`.toLowerCase().includes(q);
    });
  }, [candidates, media, search, filter]);

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageItems = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const columns = Math.max(1, Math.ceil(Math.sqrt(pageItems.length)));
  const rows = Math.max(1, Math.ceil(pageItems.length / columns));
  const compact = columns >= 6;

  const focusedIndex = visible.findIndex((c) => c.identity === focusedId);
  const focused = focusedIndex >= 0 ? visible[focusedIndex] : undefined;
  const focusedMedia = focused ? media[focused.identity] : undefined;

  const step = useCallback(
    (delta: number) => {
      if (visible.length === 0 || focusedIndex < 0) return;
      setFocusedId(visible[(focusedIndex + delta + visible.length) % visible.length].identity);
      setAudioOn(false);
    },
    [visible, focusedIndex]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!focusedId) return;
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "Escape") setFocusedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedId, step]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wallRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else wallRef.current?.requestFullscreen();
  };

  if (isLoading) return <Spin style={{ display: "block", marginTop: 80 }} />;
  if (isError || !session) return <Alert type="error" showIcon message={t("proctoring.connectionError")} />;

  return (
    <div
      ref={wallRef}
      data-testid="proctoring-wall"
      data-received-videos={receivedVideos}
      style={{
        display: "flex",
        flexDirection: "column",
        height: isFullscreen ? "100vh" : "calc(100vh - 120px)",
        background: "#0b0b0b",
        padding: 8,
        gap: 8,
        borderRadius: isFullscreen ? 0 : 8,
      }}
    >
      <Flex gap={8} align="center" wrap="wrap">
        <Tooltip title={t("proctoring.back")}>
          <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(`/${getKeycloakRealm}/proctoring`)} />
        </Tooltip>
        <Typography.Text strong style={{ color: "#fff", fontSize: 16 }}>
          {session.examTitle}
        </Typography.Text>
        <Tag color={status === "connected" ? "green" : status === "error" ? "red" : "default"} data-testid="proctoring-status">
          {t("proctoring.connected")}: {connectedCount}/{candidates.length}
        </Tag>
        <Input.Search
          allowClear
          placeholder={t("proctoring.search")}
          style={{ width: 220 }}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Segmented<Filter>
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
          options={[
            { value: "all", label: t("proctoring.filterAll") },
            { value: "connected", label: t("proctoring.filterConnected") },
            { value: "disconnected", label: t("proctoring.filterDisconnected") },
          ]}
        />
        <Select
          value={pageSize}
          style={{ width: 130 }}
          onChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
          options={PAGE_SIZES.map((n) => ({ value: n, label: n === 100 ? t("proctoring.all") : `${n} ${t("proctoring.perPage")}` }))}
        />
        <Pagination simple current={currentPage} total={visible.length} pageSize={pageSize} onChange={setPage} />
        <Flex gap={8} style={{ marginLeft: "auto" }}>
          {session.canManageAssignments && (
            <Button icon={<Users size={16} />} onClick={() => setAssignOpen(true)} data-testid="open-assignments">
              {t("proctoring.assignments")}
            </Button>
          )}
          <Button icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />} onClick={toggleFullscreen}>
            {isFullscreen ? t("proctoring.exitFullscreen") : t("proctoring.fullscreen")}
          </Button>
        </Flex>
      </Flex>

      {error && <Alert type="error" showIcon message={`${t("proctoring.connectionError")}: ${error}`} />}

      {focused ? (
        <Flex style={{ flex: 1, minHeight: 0 }} gap={8}>
          <Button type="text" icon={<ChevronLeft color="#fff" />} onClick={() => step(-1)} style={{ alignSelf: "center" }} />
          <div style={{ flex: 1, minHeight: 0, position: "relative", borderRadius: 8, overflow: "hidden", background: "#000" }} data-testid="proctor-focus">
            {focusedMedia?.video ? (
              <CandidateVideo track={focusedMedia.video} fit="contain" />
            ) : (
              <Flex align="center" justify="center" style={{ height: "100%", color: "#8c8c8c" }}>
                {focusedMedia?.connected ? t("proctoring.noVideo") : t("proctoring.notConnected")}
              </Flex>
            )}
            {audioOn && focusedMedia?.audio && <CandidateAudio track={focusedMedia.audio} />}
            <Flex align="center" gap={8} style={{ position: "absolute", left: 12, right: 12, bottom: 12 }}>
              <Typography.Text style={{ color: "#fff", fontSize: 18 }} strong>
                {candidateName(focused)}
              </Typography.Text>
              {examStateTag(focused.examInstanceState, t)}
              {focused.assessorName && <Tag>{focused.assessorName}</Tag>}
              <Flex gap={8} style={{ marginLeft: "auto" }} align="center">
                <Typography.Text style={{ color: "#bfbfbf" }}>{t("proctoring.focusHint")}</Typography.Text>
                <Button
                  icon={audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  disabled={!focusedMedia?.audio}
                  onClick={() => setAudioOn((v) => !v)}
                >
                  {audioOn ? t("proctoring.mute") : t("proctoring.unmute")}
                </Button>
                <Button onClick={() => setFocusedId(null)}>{t("proctoring.back")}</Button>
              </Flex>
            </Flex>
          </div>
          <Button type="text" icon={<ChevronRight color="#fff" />} onClick={() => step(1)} style={{ alignSelf: "center" }} />
        </Flex>
      ) : pageItems.length === 0 ? (
        <Empty description={<span style={{ color: "#8c8c8c" }}>{t("proctoring.noCandidates")}</span>} style={{ marginTop: 80 }} />
      ) : (
        <div
          data-testid="proctor-grid"
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gap: 6,
            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
          }}
        >
          {pageItems.map((c) => (
            <CandidateTile
              key={c.identity}
              candidate={c}
              media={media[c.identity]}
              compact={compact}
              showAssessor={session.canManageAssignments}
              onClick={() => {
                setFocusedId(c.identity);
                setAudioOn(false);
              }}
            />
          ))}
        </div>
      )}

      {/* In fullscreen only the wall element is painted, so the drawer must be mounted inside it. */}
      {session.canManageAssignments && termId && (
        <ProctorAssignmentsDrawer
          examTermId={termId}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          getContainer={isFullscreen ? () => wallRef.current ?? document.body : undefined}
        />
      )}
    </div>
  );
};
